import { useCallback, useEffect, useMemo, useState } from "react";

import { useMqttContext } from "../contexts/MqttContext";
import { RECENT_LAP_TIMES } from "../lib/fuelProjection";
import type {
  RaceChronoConnectionState,
  RaceChronoLapTimingState,
  RaceChronoMonitorStatus,
} from "../types/racechrono";

const STALE_MONITOR_MS = 6000;

const emptyStatus: RaceChronoMonitorStatus = {
  connected: false,
  ready: false,
  indicateEnabled: false,
  configuredMonitors: 0,
  totalMonitors: 0,
  configError: false,
  lastValueUpdateMs: 0,
  lastConfigAttemptMs: 0,
  lastAckMs: 0,
  lastErrorCode: 0,
  lastEquationErrorType: 0,
  lastEquationErrorPosition: 0,
  lastEquationErrorLength: 0,
  rawState: 0,
};

interface InternalRaceChronoState {
  lapNumber: number | null;
  currentLapTimeMs: number | null;
  previousLapTimeMs: number | null;
  bestLapTimeMs: number | null;
  deltaLapTimeMs: number | null;
  lapTimesMs: number[];
  lastUpdateMs: number | null;
  status: RaceChronoMonitorStatus;
}

function deriveState(status: RaceChronoMonitorStatus, lastUpdateMs: number | null): RaceChronoConnectionState {
  if (status.configError) {
    return "error";
  }
  if (!status.connected) {
    return "disconnected";
  }
  if (!status.ready) {
    return "configuring";
  }
  if (lastUpdateMs !== null && Date.now() - lastUpdateMs <= STALE_MONITOR_MS) {
    return "ready";
  }
  if (lastUpdateMs !== null) {
    return "stale";
  }
  return "ready";
}

export function useRaceChronoLapTiming(topicPrefix: string): RaceChronoLapTimingState {
  const { subscribe } = useMqttContext();
  const [internal, setInternal] = useState<InternalRaceChronoState>({
    lapNumber: null,
    currentLapTimeMs: null,
    previousLapTimeMs: null,
    bestLapTimeMs: null,
    deltaLapTimeMs: null,
    lapTimesMs: [],
    lastUpdateMs: null,
    status: emptyStatus,
  });
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  const handleMessage = useCallback(
    (topic: string, payload: string) => {
      if (!topic.startsWith(`${topicPrefix}racechrono/`)) {
        return;
      }

      if (topic === `${topicPrefix}racechrono/monitor/status`) {
        try {
          const parsed = JSON.parse(payload) as Record<string, unknown>;
          setInternal((prev) => ({
            ...prev,
            status: {
              connected: parsed.connected === true,
              ready: parsed.ready === true,
              indicateEnabled: parsed.indicate_enabled === true,
              configuredMonitors: Number(parsed.configured_monitors ?? 0),
              totalMonitors: Number(parsed.total_monitors ?? 0),
              configError: parsed.config_error === true,
              lastValueUpdateMs: Number(parsed.last_value_update_ms ?? 0),
              lastConfigAttemptMs: Number(parsed.last_config_attempt_ms ?? 0),
              lastAckMs: Number(parsed.last_ack_ms ?? 0),
              lastErrorCode: Number(parsed.last_error_code ?? 0),
              lastEquationErrorType: Number(parsed.last_equation_error_type ?? 0),
              lastEquationErrorPosition: Number(parsed.last_equation_error_position ?? 0),
              lastEquationErrorLength: Number(parsed.last_equation_error_length ?? 0),
              rawState: Number(parsed.state ?? 0),
            },
          }));
        } catch {
          // Ignore malformed status JSON.
        }
        return;
      }

      const value = Number(payload);
      if (!Number.isFinite(value)) {
        return;
      }

      setInternal((prev) => {
        const next: InternalRaceChronoState = {
          ...prev,
          lastUpdateMs: Date.now(),
        };
        switch (topic) {
          case `${topicPrefix}racechrono/lap/number`:
            next.lapNumber = value;
            break;
          case `${topicPrefix}racechrono/lap/time_ms`:
            next.currentLapTimeMs = value;
            break;
          case `${topicPrefix}racechrono/lap/previous_ms`:
            next.previousLapTimeMs = value;
            if (value > 0 && value !== prev.previousLapTimeMs) {
              next.lapTimesMs = [...prev.lapTimesMs, value].slice(-RECENT_LAP_TIMES);
            }
            break;
          case `${topicPrefix}racechrono/lap/best_ms`:
            next.bestLapTimeMs = value;
            break;
          case `${topicPrefix}racechrono/lap/delta_ms`:
            next.deltaLapTimeMs = value;
            break;
          default:
            return prev;
        }
        return next;
      });
    },
    [topicPrefix],
  );

  useEffect(() => subscribe(handleMessage), [subscribe, handleMessage]);

  return useMemo(
    () => ({
      lapNumber: internal.lapNumber,
      currentLapTimeMs: internal.currentLapTimeMs,
      previousLapTimeMs: internal.previousLapTimeMs,
      bestLapTimeMs: internal.bestLapTimeMs,
      deltaLapTimeMs: internal.deltaLapTimeMs,
      lapTimesMs: internal.lapTimesMs,
      lastUpdateMs: internal.lastUpdateMs,
      state: deriveState(internal.status, internal.lastUpdateMs),
      status: internal.status,
    }),
    [internal, now],
  );
}
